import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Form, Col, Row, Input, Button, Checkbox } from 'antd';
import Spin from 'src/components/common/Spin';
import { FormComponentProps } from 'antd/lib/form';
import { useFetchProxyCredential } from 'src/api/proxyService';
import { ProxyCredential } from 'src/types/ProxyServiceInfo';
import styles from './EditConfigurationServiceForm.module.less';

type CheckboxField = {
  label: string;
  field: keyof ProxyCredential;
};

type EditConfigurationServiceFormProps = FormComponentProps & {
  onSave: (values: ProxyCredential) => void;
  proxyId: number;
};

const checkboxFields: CheckboxField[] = [
  { label: 'IP Rotation', field: 'ipRotation' },
  { label: 'API Service', field: 'apiService' },
  { label: 'Perf Formula', field: 'perfFormula' },
  { label: 'Geo Perf Formula', field: 'geoPerfFormula' },
  { label: 'External Lookup', field: 'externalLookup' },
  { label: 'Free Service', field: 'freeService' },
  { label: 'Simultaneous Proxies', field: 'simultaneousProxies' },
  { label: 'Do Not Block', field: 'doNotBlock' },
];

const formConfig = {
  proxyServiceName: {
    rules: [{ required: true, whitespace: true, message: 'Proxy Service Name is required!' }],
    validateTrigger: 'onBlur',
  },
  Port: { rules: [{ required: true, message: 'Port is required!' }], validateTrigger: 'onBlur' },
  Maxproxies: { rules: [{ required: false, message: 'Max Proxies is required!' }], validateTrigger: 'onBlur' },
};

const EditConfigurationServiceForm: React.FC<EditConfigurationServiceFormProps> = ({
  form,
  onSave,
  proxyId,
}: EditConfigurationServiceFormProps) => {
  const { getFieldDecorator, getFieldsValue } = form;
  const { loading, proxyCredential } = useFetchProxyCredential(proxyId);
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);

  const initialFormState: ProxyCredential = {
    proxyId: 0,
    proxyServiceName: '',
    port: '',
    maximumProxies: '',
    ipRotation: false,
    apiService: false,
    perfFormula: false,
    geoPerfFormula: false,
    externalLookup: false,
    freeService: false,
    simultaneousProxies: false,
    doNotBlock: false,
  };

  const [formFields, setFormFields] = useState(initialFormState);

  useEffect(() => {
    if (proxyCredential && !loading) {
      const updatedFormFields = { ...initialFormState, ...proxyCredential };
      setFormFields(updatedFormFields);
    }
  }, [loading, proxyCredential]);

  const handleFieldChange = () => {
    setIsFieldsTouched(true);
  };

  const disableSubmit = () => {
    const { proxyServiceName } = getFieldsValue();
    return !isFieldsTouched || !proxyServiceName;
  };

  const handleSubmit = (event: SyntheticEvent): void => {
    event.preventDefault();
    form.validateFields((err, values: ProxyCredential) => {
      if (!err) {
        onSave({ ...values, proxyId });
      }
    });
  };

  const handleCheckboxChange = (field: keyof ProxyCredential, value: boolean): void => {
    setFormFields({ ...formFields, [field]: value });
    setIsFieldsTouched(true);
  };

  return (
    <div className={styles.configServiceFormWrapper}>
      <h1 className={styles.configServiceFormTitle}>Edit Proxy Configuration</h1>
      <Spin spinning={loading}>
        <Form layout="vertical" hideRequiredMark onSubmit={handleSubmit}>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label={<h6 className={styles.fromLabel}>Proxy Service Name:</h6>}
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 20 }}
              >
                {getFieldDecorator('proxyServiceName', {
                  ...formConfig.proxyServiceName,
                  initialValue: formFields.proxyServiceName,
                })(<Input type="text" placeholder="Please enter Proxy Service Name" onChange={handleFieldChange} />)}
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<h6 className={styles.fromLabel}>Port:</h6>}
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 20 }}
              >
                {getFieldDecorator('port', {
                  ...formConfig.Port,
                  initialValue: formFields.port,
                })(<Input type="text" placeholder="Please enter Port" onChange={handleFieldChange} />)}
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<h6 className={styles.fromLabel}>Max Proxies:</h6>}
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 20 }}
              >
                {getFieldDecorator('maximumProxies', {
                  ...formConfig.Maxproxies,
                  initialValue:
                    formFields.maximumProxies === 'null' || formFields.maximumProxies === '0'
                      ? null
                      : formFields.maximumProxies,
                })(<Input type="text" placeholder="#Max Proxies" onChange={handleFieldChange} />)}
              </Form.Item>
            </Col>
          </Row>
          {checkboxFields.map(({ label, field }) => (
            <Col span={24} key={field}>
              <Form.Item
                className="ant-col-12"
                label={<h6 className={styles.checkBoxLabel}>{label}:</h6>}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 4 }}
              >
                {getFieldDecorator(field, { valuePropName: 'checked', initialValue: formFields[field] })(
                  <Checkbox onChange={(e) => handleCheckboxChange(field, e.target.checked)} />
                )}
              </Form.Item>
            </Col>
          ))}
          <Row>
            <Col span={24} className={styles.saveButtonCol}>
              <Col span={24}>
                <Button block type="primary" htmlType="submit" disabled={disableSubmit()} className={styles.saveButton}>
                  Save
                </Button>
              </Col>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

export default Form.create<EditConfigurationServiceFormProps>({ name: 'name' })(EditConfigurationServiceForm);
